import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { NavLink } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { pathOr } from 'ramda';
import classNames from 'classnames/bind';

// Utils
import {
  formatPhone,
  formatDate,
  formatCurrency,
  formatPrice,
} from 'utils/formatString';
import * as dvr from 'utils/dvr';
import { pushBlockedCtnActionEvent } from 'utils/ga/home';
import { openPopup } from 'actions';
import {
  DataBlocks,
  Link,
  Icon,
  I18n,
  Tooltip,
  ErrorBlock,
  Status,
} from 'components';

// Actions
import { refetchData } from '../../actions';

// Analytics
import { tariffAnalytics } from '../../analytics';

// Components

import PaymentDetails from './components/PaymentDetails';
import FavoriteNumberWizardModal from './components/FavoriteNumberWizardModal';
import { getMessage } from '../../../../components/I18n';

// Hocs
import FavoriteNumberWizardModalHoc from './hocs/FavoriteNumberWizardModalHoc';

// Styles
import styles from './styles.pcss';

const cx = classNames.bind(styles);

class Summary extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isFavoriteModalOpen: false,
    };
  }

  onToggleFavoriteNumberModal = () => this.setState((prevState) => ({
    isFavoriteModalOpen: !prevState.isFavoriteModalOpen,
  }));

  pushClickAnalytics(data) {
    tariffAnalytics.pushClickEvent(data);
  }

  renderFavoriteNumbers = () => {
    const { isFavoriteModalOpen } = this.state;
    const {
      favoriteNumbers,
      ctn,
    } = this.props.data;
    const favoriteNumbersLength = favoriteNumbers && favoriteNumbers.length;

    return (
      <>
        <tr className={cx('profileInfoRow')}>
          <td className={cx('profileInfoLabel')}>
            Любимый номер
          </td>
          <td className={cx('profileInfoValue')}>
            <Link onClick={this.onToggleFavoriteNumberModal}>
              {(favoriteNumbersLength > 0) ? formatPhone(favoriteNumbers[0]) : 'Добавить'}
            </Link>
            <FavoriteNumberWizardModalHoc
              ctn={ctn}
              opened={isFavoriteModalOpen}
              closeModal={this.onToggleFavoriteNumberModal}
            >
              <FavoriteNumberWizardModal />
            </FavoriteNumberWizardModalHoc>
          </td>
        </tr>
      </>
    );
  };

  renderFavoriteStatus = () => (
    <Status
      className={['attention', 'compact', cx('favoriteNumberStatus')]}
      emoji="pointingHandUp"
    >
      В вашем тарифе есть возможность звонить на любимый номер!
      <Tooltip>
        При выборе номера билайн в качестве любимого звонки не расходуют пакет.
        <br />
        <br />
        При выборе номера другого оператора в качестве любимого звонки не расходуют пакет.
        После 1000 мин/мес звонки тарифицируются поминутно, согласно тарифу.
      </Tooltip>
    </Status>
  );

  render() {
    const {
      data,
      error,
      actions,
      isRestrictedAccess,
      isConvergent,
      isPorted,
      settingsRouteAvailable,
      yandexTariffContent,
      isPartnershipTariffConnected,
    } = this.props;

    if (error && error.type) {
      return (
        <ErrorBlock
          className="halfWidth"
          title={<I18n path="home.summary.blockTitle" />}
          refreshAction={() => actions.refetchData('ProfileSummary')}
        />
      );
    }

    if (!data) {
      return null;
    }
    const isBlocked = data.status && data.status !== 'Active';
    const isBlockedByBalance = data.status && data.status !== 'Active' && !data.canChangeStatus;

    const dateBlock = new Date(data.dateBlock);
    const dateUnblock = new Date(data.dateUnblock);
    const today = new Date();
    const isFutureRequestActive = (dateBlock > today || dateUnblock > today);

    const favoriteNumber = data.favoriteNumbers;
    const favoriteNumbersLength = favoriteNumber && favoriteNumber.length;

    const showConvergenceLink = isConvergent && !data.isTariffUP;

    const blockLinks = [];
    if (data.futureTariff?.isCancelTransferAllowed) {
      blockLinks.push({
        children: <I18n path="home.summary.cancelFutureTariff" />,
        onClick: () => actions.openPopup('cancelFutureTariff'),
      });
    }
    if (isConvergent) {
      blockLinks.push({
        children: <I18n path="home.summary.anotherPayments" />,
        to: 'services',
      });
    }

    if (!isRestrictedAccess && data.status && !isFutureRequestActive && !isBlocked) {
      blockLinks.push({
        children: <I18n path="home.summary.blockButton" />,
        onClick: () => {
          pushBlockedCtnActionEvent('blockCtn', null, true);
          actions.openPopup('blockCtn');
        },
      });
    }
    if (!isRestrictedAccess && isBlocked && !isFutureRequestActive && !isBlockedByBalance) {
      blockLinks.push({
        children: <I18n path="home.summary.unblockButton" />,
        onClick: () => {
          pushBlockedCtnActionEvent('unblockCtn', null, true);
          actions.openPopup('unblockCtn');
        },
      });
    }
    if (isFutureRequestActive) {
      blockLinks.push({
        children: <I18n path="home.summary.cancelFutureStatusRequestButton" />,
        onClick: () => {
          pushBlockedCtnActionEvent('cancelFutureStatusRequest', null, true);
          actions.openPopup('cancelFutureStatusRequest');
        },
      });
    }

    if (!isConvergent) {
      blockLinks.push({
        children: <I18n path="home.summary.catalogLink" />,
        href: data.tariffCatalogUrl,
        onClick: () => {
          this.pushClickAnalytics({
            [getMessage('home.summary.catalogLink')]: data.tariffCatalogUrl,
          });
        },
      });
    }

    return (
      <DataBlocks.Block
        className={['halfWidth', { highlighted: !isBlocked }]}
        links={!isPorted && blockLinks}
        ariaLabel="Информация об аккаунте"
        title={(
          <div className={cx('summary-header')}>
            <span className={cx('profileInfoAlias')}>
              {isPorted ? 'Мобильная связь' : (data.nickname || formatPhone(data.ctn))}
            </span>
            {!isConvergent && (
              <Icon className={cx('profileInfoIcon')} name="smartphone" />
            )}
            {settingsRouteAvailable && (
              <NavLink
                className={cx('edit-login')}
                to={{
                  pathname: '/settings/number-management',
                  params: { activeInput: 'nickName' },
                }}
              >
                <Icon name="pen" className={cx('edit-login__icon')} />
              </NavLink>
            )}
          </div>
        )}
      >
        <table className={cx('profileInfo')}>
          <tbody>
            {isConvergent && (
            <tr className={cx('profileInfoRow')}>
              <td className={cx('profileInfoLabel')}>
                <I18n path="home.summary.balance" />
              </td>
              <td className={cx('profileInfoValue')}>
                {formatPrice(data.balance)}
                {' '}
                ₽
              </td>
            </tr>
            )}

            {!isConvergent && (
            <tr className={cx('profileInfoRow')}>
              <td className={cx('profileInfoLabel')}>
                <I18n path="home.summary.ctnLabel" />
              </td>
              <td className={cx('profileInfoValue')}>
                {formatPhone(data.ctn)}
              </td>
            </tr>
            )}

            {isPorted && data.portedDate && (
            <tr className={cx('profileInfoRow')}>
              <td className={cx('profileInfoLabel')}>
                <I18n path="home.summary.portedLabel" />
              </td>
              <td className={cx('profileInfoValue')}>
                {formatDate(data.portedDate)}
              </td>
            </tr>
            )}

            {data.tariffRcRateText && (
            <PaymentDetails
              showConvergenceLink={showConvergenceLink}
              details={{
                tariffRcRateText: data.tariffRcRateText,
                tariffRcRateTooltip: data.tariffRcRateTooltip,
                tariffRcRateExtras: data.tariffRcRateExtras,
                discountParams: data.discountParams,
              }}
              yandexTariffContent={yandexTariffContent}
              isPartnershipTariffConnected={isPartnershipTariffConnected}
            />
            )}

            {isConvergent && (
            <>
              {data.amountToPay > 0 ? (
                <>
                  <tr className={cx('profileInfoRow')}>
                    <td className={cx('profileInfoLabel')}>
                      <I18n path={isConvergent ? 'home.summary.sumToBeConvergent' : 'home.summary.sumToBePaid'} />
                    </td>
                    <td className={cx('profileInfoValue')}>
                      {formatPrice(Math.ceil(data.amountToPay))}
                      {' ₽'}
                      {data.recommendationToPayTooltip && (
                        <Tooltip>
                          {data.recommendationToPayTooltip}
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                  {data.nextBillingDate && (
                    <tr className={cx('profileInfoRow')}>
                      <td className={cx('profileInfoLabel')}>
                        <I18n
                          path={isConvergent ? 'home.summary.nextBillingDateConvergent' : 'home.summary.nextBillingDate'}
                        />
                      </td>
                      <td className={cx('profileInfoValue')}>
                        {formatDate(data.nextBillingDate)}
                      </td>
                    </tr>
                  )}
                </>
              ) : (
                data.nextBillingDate && (
                  <tr className={cx('profileInfoRow')}>
                    <td className={cx('profileInfoMessage')} colSpan="2">
                      <I18n
                        path="home.summary.periodIsPaid"
                        vars={{ date: formatDate(data.nextBillingDate) }}
                      />
                    </td>
                  </tr>
                )
              )}
            </>
            )}

            {data.tariffName && (
            <tr className={cx('profileInfoRow')}>
              <td className={cx('profileInfoLabel')}>
                <I18n path="home.summary.tariffLabel" />
              </td>
              <td className={cx('profileInfoValue')}>
                <Link
                  href={dvr.guid ? null : data.tariffUrl}
                  onClick={() => {
                    this.pushClickAnalytics({ [data.tariffName]: data.tariffUrl });
                    dvr.playWaw('Idr_vash_tp.wav', data.tariffUrl);
                  }}
                >
                  {data.tariffName}
                </Link>
              </td>
            </tr>
            )}

            {data.futureTariff && (
            <tr className={cx('profileInfoRow')}>
              <td className={cx('profileInfoLabel')}>
                <I18n path="home.summary.futureTariffLabel" />
              </td>
              <td className={cx('profileInfoValue')}>
                <Link href={data.futureTariff.url}>
                  {data.futureTariff.name}
                </Link>
                {data.futureTariff.isCancelTransferAllowed && (
                  <Tooltip>
                    <I18n
                      path="home.summary.futureTariffDate"
                      vars={{ date: formatDate(data.futureTariff.date) }}
                    />
                  </Tooltip>
                )}
              </td>
            </tr>
            )}

            {data.nrtbcBalanceProfile && data.nrtbcBalanceProfile.value && (
            <tr>
              <td className={cx('profileInfoLabel')}>
                <I18n path="home.summary.spendingLimitLabel" />
              </td>
              <td className={cx('profileInfoValue')}>
                {formatCurrency(`${data.nrtbcBalanceProfile.value} ${data.nrtbcBalanceProfile.currency}`)}
                <Tooltip>
                  <I18n path="home.summary.spendingLimitHint" />
                </Tooltip>
              </td>
            </tr>
            )}

            {isBlockedByBalance && (
            <tr className={cx('profileInfoRow')}>
              <td className={cx('profileInfoStatus')} colSpan="2">
                <div className={cx('blockedMessage')}>
                  <span className={cx('roundIcon')}>
                    <Icon className={cx('profileInfoDeadIcon')} name="smile-dead" />
                  </span>
                  <I18n path="home.summary.blockedMessage" />
                </div>
              </td>
            </tr>
            )}

            {isFutureRequestActive && (
            <tr>
              <td className={cx('profileInfoStatus')} colSpan="2">
                <div className={cx('futureRequestMessage')}>
                  <span className={cx('roundIcon')}>
                    <Icon className={cx('profileInfoDeadIcon')} name="smile-dead" />
                  </span>
                  {dateBlock > today && (
                    <I18n
                      path="home.summary.futureBlockMessage"
                      vars={{ date: formatDate(data.dateBlock) }}
                    />
                  )}
                  {dateUnblock > today && (
                    <I18n
                      path="home.summary.futureUnblockMessage"
                      vars={{ date: formatDate(data.dateUnblock) }}
                    />
                  )}
                </div>
              </td>
            </tr>
            )}

            {favoriteNumber && (
              this.renderFavoriteNumbers()
            )}
          </tbody>
        </table>
        {(favoriteNumbersLength === 0) && (
          this.renderFavoriteStatus()
        )}
      </DataBlocks.Block>
    );
  }
}

Summary.propTypes = {
  data: PropTypes.object,
  error: PropTypes.object,
  actions: PropTypes.object,
  isRestrictedAccess: PropTypes.bool,
  isConvergent: PropTypes.bool,
  isPorted: PropTypes.bool,
  settingsRouteAvailable: PropTypes.bool,
  isPartnershipTariffConnected: PropTypes.bool,
  yandexTariffContent: PropTypes.object,
};

const mapStateToProps = ({
  home,
  application,
}) => {
  const hasError = !home.isUserInfoFetching && home.userInfoFetchingError !== null;

  if (hasError) {
    return {
      error: {
        type: 'Fetching user info error',
      },
    };
  }

  const profileSummaryData = pathOr({}, ['profileSummary', 'data'], home);
  const nrtbcBalanceProfile = pathOr({}, ['nrtbcBalance'], profileSummaryData);
  const settingsCtn = pathOr('', ['settings', 'ctn'], application);
  const ctn = pathOr(settingsCtn, ['ctn'], profileSummaryData);
  const statusData = pathOr({}, ['status', 'data'], home);
  const balanceData = pathOr({}, ['balance', 'data'], home);
  const mainNavigation = pathOr([], ['settings', 'mainNavigation'], application);
  const settingsRouteAvailable = mainNavigation.some((navItem) => navItem?.path.includes('settings'));
  const yandexTariffContent = pathOr(null, ['tariffServices', 'data', 'yandexPlus', 'content'], home);

  const profileSummary = {
    ...home.profileSummary.data,
    ctn,
  };

  return {
    data: profileSummaryData ? Object.assign(
      { nrtbcBalanceProfile },
      ...profileSummary,
      ...statusData,
      ...balanceData,
    ) : undefined,
    error: home.profileSummary.error,
    isConvergent: application.isConvergent,
    isRestrictedAccess: application.settings.isRestrictedAccess,
    isPorted: application.isPorted,
    settingsRouteAvailable,
    isPartnershipTariffConnected: home.isPartnershipTariffConnected,
    yandexTariffContent, // Костыль для отображения тултипа на тарифе с идентиф. PartnerShipTariff
  };
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators({
    openPopup,
    refetchData,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(Summary);
